// pages/AnalysisReportPage.jsx
// Full-page visual analysis report — renders a copilot session as a formatted document.
import React, { useState, useEffect, useMemo, useCallback, useRef, useContext, createContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  copilotGetSession,
  copilotExportPptx,
  fetchBrands,
  analysisRunsList,
} from "../services/api";

// ─── Color palette ───────────────────────────────────────────────
const C = {
  navy: "#1a1f36",
  navyLight: "#252b48",
  gold: "#FFCC33",
  goldDark: "#e6b800",
  goldBg: "rgba(255, 204, 51, 0.08)",
  goldBorder: "rgba(255, 204, 51, 0.25)",
  blue: "#2563eb",
  blueBg: "#eff6ff",
  blueBorder: "#bfdbfe",
  bg: "#f8f9fc",
  white: "#ffffff",
  text: "#1e293b",
  textMuted: "#64748b",
  border: "#e2e8f0",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowLg: "0 4px 16px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)",
};

// ─── Helpers ─────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Slugify a heading title into a stable element id. Tolerant of
 *  non-ASCII chars (Romanian/Spanish/etc.) by normalising and stripping
 *  diacritics before the alnum-only filter. */
function slugifyHeading(title) {
  return (title || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// ─── Inline quote references ─────────────────────────────────────
//
// Insights & Actions tables (and a few other places) cite their
// source as "Q3 ChatGPT, Q7 Gemini, Q12 Perplexity". The string
// alone isn't very helpful — clients want to read the actual
// answer the citation refers to without scrolling back to Layer 3
// or leaving the report page. This pair of helpers wraps every
// matching citation in a small ⓘ icon that:
//   - shows a hover popover with the question + first ~400 chars
//     of the answer (peek)
//   - on click, opens a slide-in drawer with the full answer +
//     a Copy button (deep-dive)
//
// The `answer_index` payload is keyed by Q# → display LLM name
// → { question_text, answer_text }, built upstream by
// build_enhanced_context and persisted on copilot_session.

const QuoteRefContext = createContext(null);

const _LLM_DISPLAY_NAMES = [
  "ChatGPT",
  "Gemini",
  "Perplexity",
  "AI Overview",
  "Claude",
];
// Match e.g. "Q3 ChatGPT" or "Q12 AI Overview". Word-bounded on the
// left, lookahead on the right is non-letter so we don't bleed into
// the next word. Uses an alternation of the known display names so
// random "Q3 banana" doesn't get wrapped.
const _QUOTE_REF_RE = new RegExp(
  `\\bQ(\\d+)\\s+(${_LLM_DISPLAY_NAMES
    .map((n) => n.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"))
    .join("|")})(?![A-Za-z])`,
  "g",
);

function QuoteRef({ qNum, llm }) {
  const ctx = useContext(QuoteRefContext);
  const [hovering, setHovering] = useState(false);
  const entry = ctx?.answerIndex?.[`Q${qNum}`]?.[llm] || null;
  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      ctx?.openAnswer?.(qNum, llm);
    },
    [ctx, qNum, llm],
  );
  // No data for this citation? Render the bare text — the icon would
  // be misleading.
  if (!entry) {
    return <span>{`Q${qNum} ${llm}`}</span>;
  }
  const preview = (entry.answer_text || "").trim().slice(0, 360);
  const truncated = (entry.answer_text || "").length > 360;
  return (
    <span
      style={{ position: "relative", whiteSpace: "nowrap", display: "inline-flex", alignItems: "baseline", gap: 2 }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <span>{`Q${qNum} ${llm}`}</span>
      <button
        type="button"
        aria-label={`Show answer Q${qNum} ${llm}`}
        onClick={handleClick}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          marginLeft: 3,
          padding: 0,
          border: "none",
          borderRadius: "50%",
          background: "rgba(37,99,235,0.10)",
          color: "#1d4ed8",
          fontSize: 10,
          fontWeight: 700,
          fontFamily: "Georgia, serif",
          fontStyle: "italic",
          cursor: "pointer",
          lineHeight: 1,
          verticalAlign: "super",
          transition: "background 0.15s",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = "#bfdbfe";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = "rgba(37,99,235,0.10)";
        }}
      >
        i
      </button>
      {hovering && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            left: 0,
            top: "100%",
            marginTop: 6,
            zIndex: 50,
            width: 380,
            maxWidth: "min(380px, 90vw)",
            padding: "12px 14px",
            background: "#0f172a",
            color: "#f1f5f9",
            borderRadius: 8,
            boxShadow: "0 10px 24px rgba(15,23,42,0.28)",
            fontSize: 12.5,
            lineHeight: 1.55,
            whiteSpace: "normal",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#fbbf24",
              marginBottom: 4,
            }}
          >
            {`Q${qNum} • ${llm}`}
          </div>
          {entry.question_text ? (
            <div style={{ fontStyle: "italic", color: "#cbd5e1", marginBottom: 6 }}>
              {entry.question_text.slice(0, 200)}
              {entry.question_text.length > 200 ? "…" : ""}
            </div>
          ) : null}
          <div>
            {preview}
            {truncated ? "…" : ""}
          </div>
          <div
            style={{
              fontSize: 10.5,
              color: "#94a3b8",
              marginTop: 8,
              fontStyle: "italic",
            }}
          >
            Click the ⓘ for the full answer + copy
          </div>
        </span>
      )}
    </span>
  );
}

/** Walk a React children tree, replacing every "Q\d+ <LLM>" text
 *  match with a QuoteRef component. Non-string children pass through.
 *  Returns new children — same structure minus the text-match swaps. */
function renderWithQuoteRefs(children) {
  if (children == null) return children;
  if (typeof children === "string") {
    // Reset stateful lastIndex on every entry — `g`-flagged regexes
    // are stateful and a previous call could otherwise mis-position
    // the scan and miss matches at the start of `children`.
    _QUOTE_REF_RE.lastIndex = 0;
    if (!_QUOTE_REF_RE.test(children)) return children;
    _QUOTE_REF_RE.lastIndex = 0;
    const out = [];
    let lastIdx = 0;
    let m;
    let key = 0;
    while ((m = _QUOTE_REF_RE.exec(children)) !== null) {
      const before = children.slice(lastIdx, m.index);
      if (before) out.push(before);
      out.push(<QuoteRef key={`qr-${key++}`} qNum={m[1]} llm={m[2]} />);
      lastIdx = m.index + m[0].length;
    }
    const tail = children.slice(lastIdx);
    if (tail) out.push(tail);
    return out;
  }
  if (Array.isArray(children)) {
    return children.map((c, i) => {
      const r = renderWithQuoteRefs(c);
      // Preserve element keys when wrapping arrays so React doesn't
      // complain about missing keys on the inner fragments.
      if (Array.isArray(r)) {
        return <React.Fragment key={`grp-${i}`}>{r}</React.Fragment>;
      }
      return r;
    });
  }
  // React element — recurse into its children.
  if (React.isValidElement(children)) {
    const inner = children.props?.children;
    if (inner == null) return children;
    return React.cloneElement(children, undefined, renderWithQuoteRefs(inner));
  }
  return children;
}

/** Extract #, ##, ### headings from markdown to build a hierarchical TOC.
 *
 *  Levels:
 *    L1 (#)   → Audit hero anchors ("Audit 1 — Organic", "Audit 2 — Prompted").
 *               The very first # heading (the report title) is suppressed
 *               so the TOC starts with content sections.
 *    L2 (##)  → Section cards ("S1 — Brand & Competitors", "Headline KPIs",
 *               "Risks & Opportunities", "S9 — Comparison", etc.).
 *    L3 (###) → Table groups within a section ("Brand Universe — Reach",
 *               "Domains — Mentions", "Top domains — All", "Pillar 1: …").
 *
 *  ####/##### (table captions, sub-sub-headings) are rendered visually
 *  inside the card body but excluded from the TOC to keep it scannable. */
function extractSections(markdown) {
  const lines = markdown.split("\n");
  const sections = [];
  const seen = new Set();
  let firstH1Skipped = false;
  for (const line of lines) {
    const m = line.match(/^(#{1,3})\s+(.+)/);
    if (!m) continue;
    const level = m[1].length;
    const title = m[2].trim();
    // Skip only the very first `#` heading (the report title) — every
    // subsequent `#` is an audit hero and belongs in the TOC.
    if (level === 1 && !firstH1Skipped && !/^Audit\s+\d/i.test(title)) {
      firstH1Skipped = true;
      continue;
    }
    let id = slugifyHeading(title);
    // De-dupe ids in case two subsections happen to slugify identically
    // (e.g. two "S1 — Brand & Competitors" — one in Audit 1, one in Audit 2).
    let candidate = id;
    let n = 2;
    while (seen.has(candidate)) {
      candidate = `${id}-${n}`;
      n += 1;
    }
    id = candidate;
    seen.add(id);
    sections.push({ title, id, level });
  }
  return sections;
}

/** Split markdown into cards. Splits on ### (or ## if any are present —
 *  rare since the new structure rarely uses them). #### subsection
 *  headings stay inside their parent card so the per-card markdown
 *  remains a single rendered block. */
function splitIntoCards(markdown) {
  const lines = markdown.split("\n");
  const cards = [];
  let current = null;
  const seen = new Set();
  const _id = (title) => {
    let id = slugifyHeading(title);
    let candidate = id;
    let n = 2;
    while (seen.has(candidate)) {
      candidate = `${id}-${n}`;
      n += 1;
    }
    seen.add(candidate);
    return candidate;
  };

  for (const line of lines) {
    // Match # (audit hero) or ## (section card). ###/####/##### stay
    // inside the parent card so table-level headings (### Brand Universe
    // — Reach) and sub-headings (#### / #####) ride along with their
    // surrounding content instead of being orphaned in empty cards.
    const m = line.match(/^(#{1,2})\s+(.+)/);
    if (m) {
      if (current) cards.push(current);
      const title = m[2].trim();
      current = { title, id: _id(title), lines: [] };
    } else if (current) {
      current.lines.push(line);
    } else {
      // Content before the first heading — render as preamble.
      if (!cards.length && line.trim()) {
        if (!current) {
          current = { title: null, id: "preamble", lines: [] };
        }
        current.lines.push(line);
      } else if (current) {
        current.lines.push(line);
      }
    }
  }
  if (current) cards.push(current);
  return cards;
}

// ─── Markdown renderers for report styling ───────────────────────

// Click-to-sort helpers ──────────────────────────────────────────
// Tables in the report carry numeric and string columns; users want
// to sort by clicking column headers without any filter UI. The
// helpers below extract a sort key per cell, classify cells as
// numeric vs string, and pin "Total" / hidden-rows summary lines at
// the bottom regardless of direction so the bottom-line aggregates
// don't vanish into the middle of a sorted list.

const _NUMERIC_STRIP_RE = /[%,×x+\s]|pp|→/gi;

function _flattenCellText(cell) {
  if (!cell) return "";
  const text = flattenChildrenToText(cell.props ? cell.props.children : cell);
  return (text || "").trim();
}

function _isPinnedFirstCell(text) {
  // Detect "Total" / "(N rows hidden …)" / placeholder labels so the
  // sort doesn't drag them out of the bottom of the table.
  const t = (text || "").trim().replace(/^\*+|\*+$/g, "").toLowerCase();
  if (!t) return false;
  if (t.startsWith("total")) return true;
  if (t.startsWith("(") && t.includes("rows hidden")) return true;
  return false;
}

function _parseSortValue(text) {
  // Returns { num, isNum, str } — `isNum` true when the cell is a
  // numeric-looking token (after stripping units/commas/pp/etc.). The
  // raw string (lowercased, trimmed) is the fallback sort key.
  const t = (text || "").trim().replace(/^\*+|\*+$/g, "");
  if (t === "" || t === "—" || t === "-" || t === "n/a") {
    // Treat empty / dash / n/a as "least" (sort to end on asc, top on
    // desc) — meaning numerically -Infinity for asc, +Infinity for desc.
    // Caller decides via direction. Return NaN to let the comparator
    // bucket these.
    return { num: NaN, isNum: true, str: "" };
  }
  const stripped = t.replace(_NUMERIC_STRIP_RE, "");
  const n = parseFloat(stripped);
  if (!Number.isNaN(n) && /^-?\d/.test(stripped)) {
    return { num: n, isNum: true, str: t.toLowerCase() };
  }
  return { num: NaN, isNum: false, str: t.toLowerCase() };
}

function _sortRows(rows, colIdx, direction) {
  if (!rows.length || colIdx == null) return rows;
  // Split into pinned (Total / hidden-rows summary) vs sortable.
  const sortable = [];
  const pinnedBottom = [];
  rows.forEach((row) => {
    const cells = React.Children.toArray(row.props?.children || []);
    const firstText = _flattenCellText(cells[0]);
    if (_isPinnedFirstCell(firstText)) {
      pinnedBottom.push(row);
    } else {
      sortable.push(row);
    }
  });
  // Detect column type by sampling sortable rows.
  let columnIsNumeric = true;
  for (const row of sortable.slice(0, 5)) {
    const cells = React.Children.toArray(row.props?.children || []);
    const v = _parseSortValue(_flattenCellText(cells[colIdx]));
    if (v.str !== "" && !v.isNum) {
      columnIsNumeric = false;
      break;
    }
  }
  const sign = direction === "desc" ? -1 : 1;
  sortable.sort((a, b) => {
    const aCells = React.Children.toArray(a.props?.children || []);
    const bCells = React.Children.toArray(b.props?.children || []);
    const av = _parseSortValue(_flattenCellText(aCells[colIdx]));
    const bv = _parseSortValue(_flattenCellText(bCells[colIdx]));
    if (columnIsNumeric) {
      // Push NaN cells (— / n/a) to the end of the asc sort so the
      // top is always meaningful values.
      const aNan = Number.isNaN(av.num);
      const bNan = Number.isNaN(bv.num);
      if (aNan && bNan) return 0;
      if (aNan) return 1;
      if (bNan) return -1;
      return sign * (av.num - bv.num);
    }
    if (av.str < bv.str) return -1 * sign;
    if (av.str > bv.str) return 1 * sign;
    return 0;
  });
  return [...sortable, ...pinnedBottom];
}

function ReportTable({ children, ...props }) {
  const [sort, setSort] = useState(null); // { colIdx: number, direction: 'asc' | 'desc' } | null

  // Extract thead + tbody from the markdown-rendered children. The
  // top-level `children` is typically [thead, tbody] (each a React
  // element). Markdown tables sometimes interleave with whitespace
  // strings — we filter those out.
  const arr = React.Children.toArray(children);
  let theadEl = null;
  let tbodyEl = null;
  for (const c of arr) {
    if (!React.isValidElement(c)) continue;
    const isThead =
      c.type === ReportThead ||
      (typeof c.type === "string" && c.type === "thead");
    const isTbody = typeof c.type === "string" && c.type === "tbody";
    if (isThead) theadEl = c;
    else if (isTbody) tbodyEl = c;
  }

  // Body rows + sort.
  const bodyRows = tbodyEl
    ? React.Children.toArray(tbodyEl.props.children).filter((r) => React.isValidElement(r))
    : [];
  const sortedRows =
    sort && bodyRows.length > 1 ? _sortRows(bodyRows, sort.colIdx, sort.direction) : bodyRows;

  // Header cells — wrap with click + sort indicator.
  let headerRow = null;
  if (theadEl) {
    const theadKids = React.Children.toArray(theadEl.props.children).filter((r) =>
      React.isValidElement(r),
    );
    headerRow = theadKids[0] || null;
  }
  const headerCells = headerRow
    ? React.Children.toArray(headerRow.props.children).filter((c) => React.isValidElement(c))
    : [];

  const onHeaderClick = (idx) => {
    setSort((cur) => {
      if (!cur || cur.colIdx !== idx) return { colIdx: idx, direction: "asc" };
      if (cur.direction === "asc") return { colIdx: idx, direction: "desc" };
      return null; // third click clears the sort
    });
  };

  return (
    <div style={{ overflowX: "auto", margin: "16px 0", borderRadius: 8, border: `1px solid ${C.border}` }}>
      <table
        {...props}
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 14,
          lineHeight: 1.6,
        }}
      >
        {theadEl && headerRow && (
          <ReportThead>
            <tr>
              {headerCells.map((cell, idx) => {
                const isActive = sort && sort.colIdx === idx;
                const arrow = isActive ? (sort.direction === "asc" ? "▲" : "▼") : "";
                return (
                  <th
                    key={idx}
                    onClick={() => onHeaderClick(idx)}
                    title="Click to sort"
                    style={{
                      padding: "10px 14px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: 13,
                      letterSpacing: "0.02em",
                      // Explicit color + background instead of inheriting
                      // from <thead> — defensive against the active-state
                      // gold tint blending with inherited dark text and
                      // washing out the label.
                      color: "#ffffff",
                      background: isActive ? "rgba(255,204,51,0.22)" : C.navy,
                      borderBottom: isActive
                        ? `2px solid ${C.gold}`
                        : `2px solid ${C.gold}`,
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      userSelect: "none",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive)
                        e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = C.navy;
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        color: "inherit",
                      }}
                    >
                      {cell.props ? cell.props.children : cell}
                      <span
                        aria-hidden="true"
                        style={{
                          fontSize: 11,
                          opacity: 1,
                          color: isActive ? C.gold : "rgba(255,255,255,0.85)",
                          width: 10,
                          display: "inline-block",
                          fontWeight: 700,
                        }}
                      >
                        {arrow || "↕"}
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </ReportThead>
        )}
        {tbodyEl && <tbody>{sortedRows}</tbody>}
      </table>
    </div>
  );
}

function ReportThead({ children, ...props }) {
  return (
    <thead
      {...props}
      style={{
        background: C.navy,
        color: C.white,
      }}
    >
      {children}
    </thead>
  );
}

function ReportTh({ children, ...props }) {
  return (
    <th
      {...props}
      style={{
        padding: "10px 14px",
        textAlign: "left",
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: "0.02em",
        borderBottom: `2px solid ${C.gold}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function ReportTr({ children, ...props }) {
  const [hovered, setHovered] = useState(false);
  return (
    <tr
      {...props}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "rgba(37, 99, 235, 0.04)" : "transparent",
        transition: "background 0.15s",
      }}
    >
      {children}
    </tr>
  );
}

function ReportTd({ children, style: _, isHeader, ...props }) {
  return (
    <td
      {...props}
      style={{
        padding: "10px 14px",
        borderBottom: `1px solid ${C.border}`,
        fontSize: 14,
        color: C.text,
        verticalAlign: "top",
      }}
    >
      {renderWithQuoteRefs(children)}
    </td>
  );
}

function ReportBlockquote({ children, ...props }) {
  return (
    <blockquote
      {...props}
      style={{
        margin: "16px 0",
        padding: "14px 20px",
        borderLeft: `4px solid ${C.gold}`,
        background: C.goldBg,
        borderRadius: "0 8px 8px 0",
        color: C.text,
        fontStyle: "italic",
        fontSize: 15,
        lineHeight: 1.7,
      }}
    >
      {children}
    </blockquote>
  );
}

function ReportCode({ inline, className, children, ...props }) {
  const val = String(children || "");
  if (inline) {
    return (
      <code
        style={{
          background: "#f1f5f9",
          padding: "2px 6px",
          borderRadius: 4,
          fontSize: "0.9em",
          color: "#0f172a",
          fontFamily: "'SF Mono', 'Fira Code', monospace",
        }}
        {...props}
      >
        {val}
      </code>
    );
  }
  return (
    <pre
      style={{
        background: "#0f172a",
        color: "#e2e8f0",
        padding: 16,
        borderRadius: 8,
        overflow: "auto",
        fontSize: 13,
        lineHeight: 1.6,
        margin: "12px 0",
      }}
    >
      <code className={className || ""} {...props}>
        {val}
      </code>
    </pre>
  );
}

function ReportLink({ children, ...props }) {
  return (
    <a
      {...props}
      target="_blank"
      rel="noreferrer"
      style={{ color: C.blue, textDecoration: "underline", textUnderlineOffset: 2 }}
    >
      {children}
    </a>
  );
}

/** Flatten ReactMarkdown children (which can be a mix of strings + nodes)
 *  to a plain string so we can slugify a heading's text for its `id`.
 *  Without this, `<h3 id={...}>` doesn't match what extractSections
 *  produced and click-to-scroll silently fails on subsections. */
function flattenChildrenToText(children) {
  if (children == null) return "";
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(flattenChildrenToText).join("");
  if (typeof children === "object" && children.props) {
    return flattenChildrenToText(children.props.children);
  }
  return "";
}

function ReportH1({ children, ...props }) {
  // Defensive: `#` headings should be consumed as card titles by
  // splitIntoCards, but if any leak into the card body render them
  // with the same prominence as a card title.
  const id = props.id || slugifyHeading(flattenChildrenToText(children));
  return (
    <h1
      {...props}
      id={id}
      style={{
        fontSize: 24,
        fontWeight: 800,
        color: C.navy,
        margin: "28px 0 14px",
        paddingBottom: 12,
        borderBottom: `3px solid ${C.gold}`,
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
        scrollMarginTop: 96,
      }}
    >
      {children}
    </h1>
  );
}

function ReportH2({ children, ...props }) {
  // Defensive: `##` headings should be consumed as card titles. If they
  // leak through, mirror the SectionCard heading style.
  const id = props.id || slugifyHeading(flattenChildrenToText(children));
  return (
    <h2
      {...props}
      id={id}
      style={{
        fontSize: 22,
        fontWeight: 800,
        color: C.navy,
        margin: "26px 0 14px",
        paddingBottom: 12,
        borderBottom: `3px solid ${C.gold}`,
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
        scrollMarginTop: 96,
      }}
    >
      {children}
    </h2>
  );
}

function ReportH3({ children, ...props }) {
  const id = props.id || slugifyHeading(flattenChildrenToText(children));
  return (
    <h3
      {...props}
      id={id}
      style={{
        fontSize: 18,
        fontWeight: 700,
        color: C.navy,
        margin: "24px 0 12px",
        lineHeight: 1.4,
        scrollMarginTop: 96,
      }}
    >
      {children}
    </h3>
  );
}

function ReportH4({ children, ...props }) {
  const id = props.id || slugifyHeading(flattenChildrenToText(children));
  return (
    <h4
      {...props}
      id={id}
      style={{
        fontSize: 16,
        fontWeight: 600,
        color: C.navyLight,
        margin: "20px 0 8px",
        lineHeight: 1.4,
        scrollMarginTop: 96,
      }}
    >
      {children}
    </h4>
  );
}

function ReportH5({ children, ...props }) {
  // Sub-sub-section headings ("##### Brand × Competitor — Sample sentences"
  // etc.). Slightly smaller than H4 and uppercase-tracked so the visual
  // hierarchy stays clear without competing with the parent H4.
  const id = props.id || slugifyHeading(flattenChildrenToText(children));
  return (
    <h5
      {...props}
      id={id}
      style={{
        fontSize: 14,
        fontWeight: 700,
        color: C.navyLight,
        margin: "18px 0 6px",
        lineHeight: 1.4,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        scrollMarginTop: 96,
      }}
    >
      {children}
    </h5>
  );
}

function ReportStrong({ children }) {
  // Detect "insight"-style bold lines (heuristic: bold text that forms its own block)
  return <strong style={{ color: C.navy, fontWeight: 700 }}>{children}</strong>;
}

function ReportParagraph({ children, ...props }) {
  // Check if the paragraph content looks like an insight callout
  const text = String(
    Array.isArray(children) ? children.map((c) => (typeof c === "string" ? c : "")).join("") : children || ""
  );
  const isInsight =
    /^(insight|key finding|takeaway|recommendation|note|important)/i.test(text.trim());

  if (isInsight) {
    return (
      <div
        style={{
          margin: "14px 0",
          padding: "14px 18px",
          background: C.blueBg,
          border: `1px solid ${C.blueBorder}`,
          borderRadius: 8,
          fontSize: 15,
          lineHeight: 1.7,
          color: C.text,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: C.blue,
            marginRight: 10,
            verticalAlign: "middle",
            flexShrink: 0,
          }}
        />
        {renderWithQuoteRefs(children)}
      </div>
    );
  }

  return (
    <p {...props} style={{ margin: "10px 0", lineHeight: 1.75, fontSize: 15, color: C.text }}>
      {renderWithQuoteRefs(children)}
    </p>
  );
}

const markdownComponents = {
  table: ReportTable,
  thead: ReportThead,
  th: ReportTh,
  tr: ReportTr,
  td: ReportTd,
  blockquote: ReportBlockquote,
  code: ReportCode,
  a: ReportLink,
  h1: ReportH1,
  h2: ReportH2,
  h3: ReportH3,
  h4: ReportH4,
  h5: ReportH5,
  strong: ReportStrong,
  p: ReportParagraph,
  ul: ({ children }) => (
    <ul style={{ paddingLeft: 24, margin: "10px 0", lineHeight: 1.75, fontSize: 15, color: C.text }}>
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol style={{ paddingLeft: 24, margin: "10px 0", lineHeight: 1.75, fontSize: 15, color: C.text }}>
      {children}
    </ol>
  ),
  li: ({ children }) => <li style={{ marginBottom: 4 }}>{children}</li>,
  hr: () => (
    <hr
      style={{
        border: "none",
        borderTop: `2px solid ${C.border}`,
        margin: "28px 0",
      }}
    />
  ),
};

// ─── Answer drawer ───────────────────────────────────────────────
//
// Slide-in panel from the right showing the full answer text behind a
// (Q#, LLM) citation. Triggered when the user clicks the ⓘ icon next to
// any inline citation. Owns no state of its own — controlled by
// AnalysisReportPage via the open/close props so the same drawer
// instance handles citations from anywhere in the report.

function AnswerDrawer({ open, qNum, llm, entry, onClose }) {
  const [copied, setCopied] = useState(false);

  // Reset the copied-toast state every time the drawer opens for a
  // different citation, otherwise the badge would flicker as a stale
  // "Copied!" indicator on a fresh entry.
  useEffect(() => {
    setCopied(false);
  }, [qNum, llm, open]);

  // Close on Escape so power users don't have to mouse to the X.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleCopy = useCallback(async () => {
    const text = entry?.answer_text || "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API can fail in some browsers / contexts. Fall back
      // to a textarea-based copy so users always have a path.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      } catch {
        /* give up silently */
      }
      document.body.removeChild(ta);
    }
  }, [entry]);

  if (!open) return null;

  return (
    <>
      {/* backdrop — clicking outside closes */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.32)",
          zIndex: 200,
        }}
      />
      <aside
        role="dialog"
        aria-label={`Answer for Q${qNum} ${llm}`}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(560px, 96vw)",
          background: C.white,
          boxShadow: "-12px 0 40px rgba(15,23,42,0.18)",
          zIndex: 201,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "20px 24px 14px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: C.textMuted,
                marginBottom: 4,
              }}
            >
              {`Q${qNum} • ${llm}`}
            </div>
            {entry?.question_text ? (
              <h2
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: C.navy,
                  margin: 0,
                  lineHeight: 1.4,
                }}
              >
                {entry.question_text}
              </h2>
            ) : (
              <h2
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: C.textMuted,
                  margin: 0,
                  fontStyle: "italic",
                }}
              >
                (question text unavailable)
              </h2>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close answer drawer"
            style={{
              flexShrink: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 6,
              fontSize: 22,
              lineHeight: 1,
              color: C.textMuted,
              borderRadius: 6,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f1f5f9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            ×
          </button>
        </header>
        <div
          style={{
            padding: "16px 24px 14px",
            display: "flex",
            gap: 10,
            alignItems: "center",
            borderBottom: `1px solid ${C.border}`,
            background: "#f8fafc",
          }}
        >
          <button
            type="button"
            onClick={handleCopy}
            style={{
              padding: "7px 14px",
              borderRadius: 6,
              border: `1px solid ${C.border}`,
              background: copied ? "#dcfce7" : C.white,
              color: copied ? "#166534" : C.text,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {copied ? "✓ Copied" : "Copy answer"}
          </button>
          <span style={{ fontSize: 12, color: C.textMuted }}>
            Esc to close
          </span>
        </div>
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 24px 28px",
            fontSize: 14.5,
            lineHeight: 1.7,
            color: C.text,
          }}
        >
          {entry?.answer_text ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={answerDrawerMarkdownComponents}
            >
              {entry.answer_text}
            </ReactMarkdown>
          ) : (
            <span style={{ color: C.textMuted, fontStyle: "italic" }}>
              (answer text unavailable)
            </span>
          )}
        </div>
      </aside>
    </>
  );
}

// Slim subset of the report's markdown components, scoped to the
// AnswerDrawer's body. Different from the main report renderer in
// two ways: (a) we don't run renderWithQuoteRefs over the children
// — there are no nested citations to process inside an LLM answer
// — and (b) headings are rendered at smaller sizes since the drawer
// body is narrower than a section card. Also drops the "insight
// callout" auto-detection, which would mis-fire on prose.
const answerDrawerMarkdownComponents = {
  table: ReportTable,
  thead: ReportThead,
  th: ReportTh,
  tr: ReportTr,
  td: ({ children, ...props }) => (
    <td
      {...props}
      style={{
        padding: "8px 12px",
        borderBottom: `1px solid ${C.border}`,
        fontSize: 13,
        color: C.text,
        verticalAlign: "top",
      }}
    >
      {children}
    </td>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      {...props}
      style={{
        margin: "12px 0",
        padding: "10px 14px",
        borderLeft: `3px solid ${C.gold}`,
        background: C.goldBg,
        borderRadius: "0 6px 6px 0",
        color: C.text,
        fontStyle: "italic",
        fontSize: 13.5,
        lineHeight: 1.6,
      }}
    >
      {children}
    </blockquote>
  ),
  code: ({ inline, className, children, ...props }) => {
    if (inline) {
      return (
        <code
          {...props}
          style={{
            background: "#f1f5f9",
            padding: "1px 5px",
            borderRadius: 4,
            fontSize: "0.92em",
            color: "#0f172a",
            fontFamily: "'SF Mono', 'Fira Code', monospace",
          }}
        >
          {children}
        </code>
      );
    }
    return (
      <pre
        style={{
          background: "#0f172a",
          color: "#e2e8f0",
          padding: "12px 14px",
          borderRadius: 8,
          overflow: "auto",
          fontSize: 12.5,
          lineHeight: 1.55,
          margin: "10px 0",
        }}
      >
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    );
  },
  a: ({ href, children, ...props }) => (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: C.blue, textDecoration: "underline" }}
    >
      {children}
    </a>
  ),
  h1: ({ children }) => (
    <h1 style={{ fontSize: 18, fontWeight: 800, color: C.navy, margin: "16px 0 8px", lineHeight: 1.3 }}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 style={{ fontSize: 16, fontWeight: 700, color: C.navy, margin: "14px 0 6px", lineHeight: 1.3 }}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 style={{ fontSize: 15, fontWeight: 700, color: C.navy, margin: "12px 0 6px", lineHeight: 1.35 }}>{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 style={{ fontSize: 14, fontWeight: 700, color: C.navyLight, margin: "10px 0 4px", lineHeight: 1.4 }}>{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 style={{ fontSize: 13, fontWeight: 700, color: C.navyLight, margin: "8px 0 4px", textTransform: "uppercase", letterSpacing: "0.04em" }}>{children}</h5>
  ),
  strong: ({ children }) => <strong style={{ color: C.navy, fontWeight: 700 }}>{children}</strong>,
  p: ({ children, ...props }) => (
    <p {...props} style={{ margin: "8px 0", lineHeight: 1.65, fontSize: 14, color: C.text }}>
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul style={{ paddingLeft: 22, margin: "8px 0", lineHeight: 1.65, fontSize: 14, color: C.text }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ paddingLeft: 22, margin: "8px 0", lineHeight: 1.65, fontSize: 14, color: C.text }}>{children}</ol>
  ),
  li: ({ children }) => <li style={{ marginBottom: 3 }}>{children}</li>,
  hr: () => <hr style={{ border: "none", borderTop: `1px solid ${C.border}`, margin: "16px 0" }} />,
};

// ─── Admin metadata drawer ───────────────────────────────────────
//
// Slide-in panel from the right showing report metadata that doesn't
// belong in the main reading flow: cost, methodology version, audit
// trail (brand spec / models / question count snapshot), and the
// changelog. Triggered by the ⚙ icon in the header.
//
// Receives the admin-only cards filtered out of `splitIntoCards` so
// the same Audit Trail / Methodology Version content shipped in the
// MD/PPTX export is preserved here for traceability — just hidden
// from the client-facing main flow.

function AdminMetaDrawer({
  open,
  onClose,
  session,
  brandName,
  modelName,
  sessionDate,
  adminCards,
  auditQuestionsCount,
  onShowQuestions,
  onShowContext,
}) {
  // Close on Escape — same pattern as AnswerDrawer.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const audit = session?.audit;
  const cost = session?.session_cost_usd;
  const ctxKChars = session?.context_blob_chars
    ? `${(session.context_blob_chars / 1000).toFixed(0)}K chars`
    : "—";

  const _row = (label, value) => (
    <div
      key={label}
      style={{
        display: "flex",
        gap: 12,
        padding: "10px 0",
        borderBottom: `1px solid ${C.border}`,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ flex: "0 0 140px", color: C.textMuted, fontWeight: 500 }}>{label}</div>
      <div style={{ flex: 1, color: C.text, wordBreak: "break-word" }}>{value || "—"}</div>
    </div>
  );

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.32)",
          zIndex: 200,
        }}
      />
      <aside
        role="dialog"
        aria-label="Report configuration & metadata"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(620px, 96vw)",
          background: C.white,
          boxShadow: "-12px 0 40px rgba(15,23,42,0.18)",
          zIndex: 201,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "20px 24px 14px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: C.textMuted,
                marginBottom: 4,
              }}
            >
              Configuration
            </div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: C.navy,
                margin: 0,
                lineHeight: 1.4,
              }}
            >
              Report metadata &amp; audit trail
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close metadata drawer"
            style={{
              flexShrink: 0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: 6,
              fontSize: 22,
              lineHeight: 1,
              color: C.textMuted,
              borderRadius: 6,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            ×
          </button>
        </header>

        <div style={{ flex: 1, overflowY: "auto", padding: "8px 24px 28px" }}>
          {/* Top-line snapshot */}
          <section style={{ marginTop: 12 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: C.textMuted,
                marginBottom: 6,
              }}
            >
              Snapshot
            </div>
            {_row("Cost", cost != null ? `$${Number(cost).toFixed(4)}` : "—")}
            {_row("Brand", brandName)}
            {_row("Generated", sessionDate)}
            {_row("Copilot model", modelName)}
            {audit && _row("Audit", audit.title || `#${audit.id}`)}
            {audit?.created_at && _row("Audit run at", formatDate(audit.created_at))}
            {audit?.models?.length > 0 && _row("Models queried", audit.models.join(", "))}
            {_row("Context blob size", ctxKChars)}
          </section>

          {/* Inspect — debug / audit-tooling deep-dives. Open the
              existing modal pickers (questions list, layered context
              blob) which are rendered at the page root. */}
          {(onShowQuestions || onShowContext) && (
            <section style={{ marginTop: 24 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: C.textMuted,
                  marginBottom: 8,
                }}
              >
                Inspect
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {onShowQuestions && (
                  <button
                    type="button"
                    onClick={onShowQuestions}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      background: C.white,
                      color: C.text,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "inherit",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = C.white; }}
                  >
                    <span>
                      View questions
                      {typeof auditQuestionsCount === "number" && auditQuestionsCount > 0
                        ? ` (${auditQuestionsCount})`
                        : ""}
                    </span>
                    <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 400 }}>
                      Q1 — Q{auditQuestionsCount || "n"} text
                    </span>
                  </button>
                )}
                {onShowContext && (
                  <button
                    type="button"
                    onClick={onShowContext}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 14px",
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      background: C.white,
                      color: C.text,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "inherit",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = C.white; }}
                  >
                    <span>View context blob</span>
                    <span style={{ color: C.textMuted, fontSize: 12, fontWeight: 400 }}>
                      Layered LLM input ({ctxKChars})
                    </span>
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Render the Audit Trail / Methodology Version / Changelog
              cards filtered out of the main column. Same markdown,
              same components, just lives here instead of in-line. */}
          {adminCards.length > 0 && (
            <section style={{ marginTop: 28 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: C.textMuted,
                  marginBottom: 12,
                }}
              >
                Audit trail &amp; methodology
              </div>
              {adminCards.map((card, i) => (
                <details
                  key={`${card.id}-${i}`}
                  open={i === 0}
                  style={{
                    marginBottom: 12,
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    overflow: "hidden",
                  }}
                >
                  <summary
                    style={{
                      padding: "10px 14px",
                      background: "#f8fafc",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 700,
                      color: C.navy,
                    }}
                  >
                    {card.title || "Section"}
                  </summary>
                  <div style={{ padding: "10px 14px 14px", fontSize: 13 }}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {card.lines.join("\n")}
                    </ReactMarkdown>
                  </div>
                </details>
              ))}
            </section>
          )}
        </div>
      </aside>
    </>
  );
}

// ─── Section Card ────────────────────────────────────────────────

/** Hero banner used for "Audit 1 — Organic" / "Audit 2 — Prompted" boundaries.
 *  Replaces the otherwise-empty card those titles would produce, since the
 *  section cards under them already carry their own content. The banner is
 *  full-width navy with a gold underline so it visually anchors the start
 *  of each audit's group of section cards. */
function AuditBanner({ title, id }) {
  return (
    <section
      id={id}
      style={{
        background: C.navy,
        color: "#ffffff",
        borderRadius: 12,
        boxShadow: "0 6px 18px rgba(15,23,42,0.20)",
        padding: "28px 36px",
        margin: "32px 0 18px",
        borderLeft: `6px solid ${C.gold}`,
        breakInside: "avoid",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: C.gold,
          marginBottom: 6,
          opacity: 0.9,
        }}
      >
        Section
      </div>
      <h2
        style={{
          fontSize: 28,
          fontWeight: 800,
          color: "#ffffff",
          margin: 0,
          lineHeight: 1.2,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h2>
    </section>
  );
}

function SectionCard({ title, id, content }) {
  // `# Audit X — …` cards have their content shipped under the
  // `## S1 / S2 / …` cards beneath them, so the audit-titled card
  // itself is essentially empty. Render those as a hero banner
  // instead of a blank card with just a heading.
  const isAuditBanner =
    /^Audit\s+\d/i.test(title || "") &&
    (!content || !content.trim());
  if (isAuditBanner) {
    return <AuditBanner title={title} id={id} />;
  }

  return (
    <section
      id={id}
      style={{
        background: C.white,
        borderRadius: 12,
        boxShadow: C.shadow,
        padding: "32px 36px",
        marginBottom: 24,
        border: `1px solid ${C.border}`,
        breakInside: "avoid",
      }}
    >
      {title && (
        <h2
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: C.navy,
            margin: "0 0 20px",
            paddingBottom: 12,
            borderBottom: `3px solid ${C.gold}`,
            lineHeight: 1.3,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </h2>
      )}
      <div className="report-section-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {content}
        </ReactMarkdown>
      </div>
    </section>
  );
}

// ─── TOC Sidebar ─────────────────────────────────────────────────

function TableOfContents({ sections, activeId }) {
  const handleClick = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  if (!sections.length) return null;

  return (
    <nav
      className="report-toc"
      style={{
        position: "sticky",
        top: 88,
        width: 240,
        flexShrink: 0,
        maxHeight: "calc(100vh - 104px)",
        overflowY: "auto",
        paddingRight: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: C.textMuted,
          marginBottom: 12,
          paddingLeft: 14,
        }}
      >
        Contents
      </div>
      {sections.map((s) => {
        const isActive = s.id === activeId;
        // Three-level visual scheme:
        //   L1 + Audit pattern → solid navy banner (hero)
        //   L2 → primary section pill (S1 — Brand & Competitors, etc.)
        //   L3 → indented table-group entry (Brand Universe — Reach)
        const isAuditAnchor = s.level === 1 && /^Audit\s+\d/i.test(s.title);
        const isSection = s.level === 2;
        const isSub = s.level === 3;
        const maxLen = isAuditAnchor ? 32 : isSection ? 36 : 32;

        const baseFontSize = isAuditAnchor ? 13 : isSection ? 13 : 12;
        const baseFontWeight = isAuditAnchor ? 800 : isActive ? 600 : isSection ? 500 : 400;
        const baseColor = isAuditAnchor
          ? "#ffffff"
          : isActive
            ? C.navy
            : isSection
              ? C.text
              : C.textMuted;
        const padding = isAuditAnchor
          ? "11px 14px"
          : isSection
            ? "7px 14px"
            : "5px 10px 5px 28px";
        const marginTop = isAuditAnchor ? 14 : isSection ? 4 : 1;

        return (
          <button
            key={s.id}
            onClick={() => handleClick(s.id)}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              padding,
              margin: `${marginTop}px 0 ${isAuditAnchor ? 6 : 1}px 0`,
              fontSize: baseFontSize,
              lineHeight: 1.5,
              fontWeight: baseFontWeight,
              color: baseColor,
              background: isAuditAnchor
                ? C.navy
                : isActive
                  ? C.goldBg
                  : "transparent",
              borderLeft: isActive
                ? `3px solid ${C.gold}`
                : isAuditAnchor
                  ? `3px solid ${C.gold}`
                  : "3px solid transparent",
              borderTop: "none",
              borderRight: "none",
              borderBottom: "none",
              borderRadius: isAuditAnchor ? 6 : "0 6px 6px 0",
              boxShadow: isAuditAnchor ? "0 2px 6px rgba(15,23,42,0.18)" : "none",
              cursor: "pointer",
              transition: "all 0.15s",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "inherit",
              textTransform: isAuditAnchor ? "uppercase" : "none",
              letterSpacing: isAuditAnchor ? "0.06em" : "normal",
            }}
            onMouseEnter={(e) => {
              if (isAuditAnchor) {
                e.currentTarget.style.background = "#1e293b"; // slightly lighter navy
              } else if (!isActive) {
                e.currentTarget.style.background = "#f1f5f9";
              }
            }}
            onMouseLeave={(e) => {
              if (isAuditAnchor) {
                e.currentTarget.style.background = C.navy;
              } else if (!isActive) {
                e.currentTarget.style.background = "transparent";
              }
            }}
          >
            {s.title.length > maxLen ? s.title.slice(0, maxLen) + "…" : s.title}
          </button>
        );
      })}
    </nav>
  );
}

// ─── Loading / Error states ──────────────────────────────────────

function LoadingState() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: C.bg,
        color: C.textMuted,
        gap: 16,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: `3px solid ${C.border}`,
          borderTopColor: C.navy,
          animation: "reportSpin 0.8s linear infinite",
        }}
      />
      <span style={{ fontSize: 16, fontWeight: 500 }}>Loading analysis report...</span>
    </div>
  );
}

function ErrorState({ message, onBack }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        background: C.bg,
        color: C.text,
        gap: 20,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "#fef2f2",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 28,
        }}
      >
        !
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Report not available</h2>
      <p style={{ color: C.textMuted, fontSize: 15, margin: 0, maxWidth: 400, textAlign: "center" }}>
        {message || "The requested analysis session could not be loaded."}
      </p>
      {onBack && (
        <button
          onClick={onBack}
          style={{
            padding: "10px 24px",
            borderRadius: 8,
            border: "none",
            background: C.navy,
            color: C.white,
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          Go back
        </button>
      )}
    </div>
  );
}

// ─── Inject global styles (print, animations, zebra stripes) ─────

const GLOBAL_STYLE_ID = "analysis-report-styles";

function injectGlobalStyles() {
  if (document.getElementById(GLOBAL_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = GLOBAL_STYLE_ID;
  style.textContent = `
    @keyframes reportSpin {
      to { transform: rotate(360deg); }
    }

    /* Zebra striping for report tables */
    .report-section-content table tbody tr:nth-child(even) td {
      background: #f8fafc;
    }

    /* Print styles */
    @media print {
      .report-header-actions,
      .report-toc,
      .report-back-link {
        display: none !important;
      }
      .report-header {
        position: static !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .report-body {
        padding-top: 0 !important;
      }
      .report-main {
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      section {
        break-inside: avoid;
        box-shadow: none !important;
        border: 1px solid #ddd !important;
      }
    }

    /* Responsive: stack TOC above content on smaller screens */
    @media (max-width: 1100px) {
      .report-toc {
        position: static !important;
        width: 100% !important;
        max-height: none !important;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-bottom: 20px;
        padding-right: 0 !important;
      }
      .report-toc button {
        border-left: none !important;
        border-bottom: 2px solid transparent !important;
        border-radius: 6px !important;
        padding: 6px 12px !important;
        white-space: nowrap !important;
      }
      .report-layout {
        flex-direction: column !important;
      }
    }
  `;
  document.head.appendChild(style);
}

// ─── Main Component ──────────────────────────────────────────────

export default function AnalysisReportPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [auditQuestions, setAuditQuestions] = useState([]);
  const [brandName, setBrandName] = useState("");
  const [exporting, setExporting] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [showQuestionsDialog, setShowQuestionsDialog] = useState(false);
  const [showContextDialog, setShowContextDialog] = useState(false);
  const [contextLayers, setContextLayers] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [linkedRun, setLinkedRun] = useState(null); // { id, brand_id } or null
  // ── Inline-quote drawer state ──
  // Owned at the page level so a single drawer instance handles every
  // citation in the report. The QuoteRef components dispatch
  // `openAnswer(qNum, llm)` via context to set this.
  const [activeAnswer, setActiveAnswer] = useState(null); // { qNum, llm } | null
  const [showAdminDrawer, setShowAdminDrawer] = useState(false);

  const contentRef = useRef(null);

  // ── Fetch session data ──
  useEffect(() => {
    injectGlobalStyles();

    let cancelled = false;

    async function load() {
      try {
        const [data, brands] = await Promise.all([
          copilotGetSession(sessionId),
          fetchBrands().catch(() => []),
        ]);

        if (cancelled) return;

        if (!data || !data.session) {
          setError("Session not found.");
          setLoading(false);
          return;
        }

        setSession(data.session);
        setMessages(data.messages || []);
        setAuditQuestions(data.audit_questions || []);

        // Resolve brand name
        const brandId = data.session.brand_id;
        const allBrands = Array.isArray(brands) ? brands : brands?.items || [];
        const brand = allBrands.find((b) => b.id === brandId);
        setBrandName(brand?.name || `Brand #${brandId}`);
        setLoading(false);

        // Look up the analysis_run linked to this session so we can offer a
        // "Open in Analysis flow" shortcut back to the wizard. Best-effort —
        // not every session has a run (legacy sessions created before the
        // Analysis Run entity don't).
        try {
          const runs = await analysisRunsList({ brandId });
          const match = (runs?.items || []).find(
            (r) => Number(r.copilot_session_id) === Number(sessionId)
          );
          if (!cancelled && match) {
            setLinkedRun({ id: match.id, brand_id: match.brand_id });
          }
        } catch {
          /* silent */
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load session.");
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [sessionId]);

  // ── Build analysis content from assistant messages ──
  const analysisMarkdown = useMemo(() => {
    return messages
      .filter((m) => m.role === "assistant" && m.status !== "error" && (m.text || "").trim())
      .map((m) => m.text.trim())
      .join("\n\n---\n\n");
  }, [messages]);

  const sections = useMemo(() => extractSections(analysisMarkdown), [analysisMarkdown]);
  const allCards = useMemo(() => splitIntoCards(analysisMarkdown), [analysisMarkdown]);

  // Audit Trail + Methodology Version + Recent changes + Data Quality
  // Report are admin / compliance metadata that bury the substance of
  // the report when rendered inline. Filter them out of the main flow
  // and surface them behind a ⚙ settings icon in the header instead.
  const _ADMIN_TITLE_RE = /^(audit trail|methodology version|recent changes|data quality report|data quality note)\b/i;
  const { cards, adminCards } = useMemo(() => {
    const main = [];
    const admin = [];
    for (const c of allCards) {
      if (c.title && _ADMIN_TITLE_RE.test(c.title.trim())) admin.push(c);
      else main.push(c);
    }
    return { cards: main, adminCards: admin };
  }, [allCards]);

  // Drop the admin sections from the TOC too so the left rail mirrors
  // what's actually rendered in the main column.
  const tocSections = useMemo(
    () => sections.filter((s) => !_ADMIN_TITLE_RE.test((s.title || "").trim())),
    [sections],
  );

  // After ReactMarkdown renders, assign the deduplicated IDs from
  // `sections` to the rendered H4 elements in document order. Reason:
  // ReportH4's auto-id slugifies the heading text, but the same H4
  // heading appears twice in the doc (e.g. "S1 — Brand & Competitors"
  // under Audit 1 AND Audit 2). Without this fix-up, browser
  // getElementById lands on the first occurrence and clicking the TOC
  // entry for "S1" under Audit 2 scrolls to Audit 1's S1.
  useEffect(() => {
    if (!sections.length) return;
    const root = contentRef.current;
    if (!root) return;
    const t = setTimeout(() => {
      const subs = sections.filter((s) => s.level === 4);
      const h4s = Array.from(root.querySelectorAll("h4"));
      const n = Math.min(subs.length, h4s.length);
      for (let i = 0; i < n; i++) {
        h4s[i].id = subs[i].id;
      }
    }, 0);
    return () => clearTimeout(t);
  }, [sections, analysisMarkdown]);

  // ── Intersection observer for active TOC tracking ──
  useEffect(() => {
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0.1 }
    );

    const timer = setTimeout(() => {
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el) observer.observe(el);
      }
    }, 150);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [sections]);

  // ── Export handler ──
  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await copilotExportPptx(Number(sessionId));
    } catch (err) {
      alert("Export failed: " + (err?.message || "Unknown error"));
    } finally {
      setExporting(false);
    }
  }, [exporting, sessionId]);

  // ── Handle print ──
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  // ── Navigate back ──
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // ── Render states ──
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onBack={handleBack} />;
  if (!analysisMarkdown.trim()) {
    return <ErrorState message="This session has no analysis content to display." onBack={handleBack} />;
  }

  const modelName = session?.default_model || messages.find((m) => m.model)?.model || "—";
  const sessionDate = formatDate(session?.created_at);
  const sessionTitle = session?.title || "Analysis Report";

  // Quote-reference context value: gives every QuoteRef component
  // access to the answer_index lookup + the open-drawer dispatcher.
  // Memoised by reference so the (Q#, LLM) → answer subtree doesn't
  // remount on unrelated state changes.
  const quoteRefValue = {
    answerIndex: session?.answer_index || null,
    openAnswer: (qNum, llm) => setActiveAnswer({ qNum, llm }),
  };
  const activeEntry = activeAnswer
    ? (session?.answer_index?.[`Q${activeAnswer.qNum}`]?.[activeAnswer.llm] || null)
    : null;

  return (
    <QuoteRefContext.Provider value={quoteRefValue}>
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      {/* ── Header ── */}
      <header
        className="report-header"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: C.navy,
          color: C.white,
          padding: "0 32px",
          height: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 20, minWidth: 0 }}>
          <button
            className="report-back-link"
            onClick={handleBack}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255,255,255,0.7)",
              fontSize: 14,
              cursor: "pointer",
              padding: "6px 0",
              whiteSpace: "nowrap",
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = C.white; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div
            style={{
              width: 1,
              height: 28,
              background: "rgba(255,255,255,0.15)",
              flexShrink: 0,
            }}
          />

          <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: C.gold,
                flexShrink: 0,
              }}
            >
              {brandName}
            </span>
            <span style={{ color: "rgba(255,255,255,0.3)" }}>|</span>
            <span
              style={{
                fontSize: 16,
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {sessionTitle}
            </span>
          </div>
        </div>

        <div className="report-header-actions" style={{ display: "flex", gap: 10, flexShrink: 0, alignItems: "center" }}>
          <button
            type="button"
            onClick={() => setShowAdminDrawer(true)}
            title="Configuration & metadata (cost, methodology, audit trail)"
            aria-label="Open configuration & metadata"
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: C.white,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.10)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
            }}
          >
            {/* gear icon */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>

          <button
            onClick={handlePrint}
            title="Print report"
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: C.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9V2h12v7" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </button>

          <button
            onClick={async () => {
              try {
                const { copilotExportMd } = await import("../services/api");
                await copilotExportMd(sessionId);
              } catch {}
            }}
            title="Download as Markdown"
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: C.white,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Export MD
          </button>

          {linkedRun && (
            <button
              onClick={() =>
                navigate(
                  `/brands/${linkedRun.brand_id}/new-analysis?runId=${linkedRun.id}`
                )
              }
              title="Open this analysis in the 4-stage wizard (chat with MAD Pilot, revise, regenerate PPTX)"
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: `1px solid ${C.navy}`,
                background: "#fff",
                color: C.navy,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontFamily: "inherit",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = C.navy;
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.color = C.navy;
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Open in Analysis flow
            </button>
          )}

          <button
            onClick={handleExport}
            disabled={exporting}
            title="Export as PowerPoint"
            style={{
              padding: "8px 20px",
              borderRadius: 8,
              border: "none",
              background: C.gold,
              color: C.navy,
              fontSize: 13,
              fontWeight: 700,
              cursor: exporting ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "inherit",
              opacity: exporting ? 0.7 : 1,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => { if (!exporting) e.currentTarget.style.background = C.goldDark; }}
            onMouseLeave={(e) => { if (!exporting) e.currentTarget.style.background = C.gold; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {exporting ? "Exporting..." : "Export PPTX"}
          </button>
        </div>
      </header>

      {/* ── Body: TOC sidebar + main content ── */}
      <div
        className="report-body"
        style={{
          paddingTop: 32,
          paddingBottom: 64,
        }}
      >
        <div
          className="report-layout"
          style={{
            display: "flex",
            gap: 32,
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 32px",
            alignItems: "flex-start",
          }}
        >
          {tocSections.length > 1 && (
            <TableOfContents sections={tocSections} activeId={activeSection} />
          )}

          <main
            className="report-main"
            ref={contentRef}
            style={{
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Title card */}
            <div
              style={{
                background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyLight} 100%)`,
                borderRadius: 12,
                padding: "36px 40px",
                marginBottom: 28,
                color: C.white,
                boxShadow: C.shadowLg,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  color: C.gold,
                  marginBottom: 8,
                }}
              >
                MAD Discovery Analysis Report
              </div>
              <h1
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  margin: "0 0 12px",
                  lineHeight: 1.25,
                  letterSpacing: "-0.02em",
                }}
              >
                {sessionTitle}
              </h1>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 14, color: "rgba(255,255,255,0.7)" }}>
                <span>
                  <strong style={{ color: C.white, fontWeight: 600 }}>Brand:</strong> {brandName}
                </span>
                <span>
                  <strong style={{ color: C.white, fontWeight: 600 }}>Model:</strong> {modelName}
                </span>
                <span>
                  <strong style={{ color: C.white, fontWeight: 600 }}>Date:</strong> {sessionDate}
                </span>
                {/* Cost intentionally omitted from the client-facing title
                    card — it lives in the ⚙ admin drawer only. */}
              </div>
            </div>

            {/* Section cards */}
            {cards.map((card, i) => (
              <SectionCard
                key={card.id + "-" + i}
                title={card.title}
                id={card.id}
                content={card.lines.join("\n")}
              />
            ))}

            {/* Footer */}
            <div
              style={{
                textAlign: "center",
                padding: "32px 0 0",
                color: C.textMuted,
                fontSize: 13,
              }}
            >
              Generated by MAD Discovery — {sessionDate}
            </div>
          </main>
        </div>
      </div>

      {/* Questions Dialog */}
      {showQuestionsDialog && (
        <div onClick={() => setShowQuestionsDialog(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", zIndex: 1000, display: "grid", placeItems: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, maxWidth: 700, width: "100%", maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: C.navy }}>Audit Questions</h2>
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{auditQuestions.length} questions used in this audit</p>
              </div>
              <button onClick={() => setShowQuestionsDialog(false)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#9ca3af", padding: "4px 8px" }}>&times;</button>
            </div>
            <div style={{ overflow: "auto", padding: "16px 24px" }}>
              {auditQuestions.length === 0 ? (
                <p style={{ color: "#9ca3af", fontSize: 13 }}>No questions available for this session.</p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 11, textTransform: "uppercase", borderBottom: "2px solid #e5e7eb", width: 50 }}>#</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 11, textTransform: "uppercase", borderBottom: "2px solid #e5e7eb" }}>Question</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 11, textTransform: "uppercase", borderBottom: "2px solid #e5e7eb", width: 60 }}>Lang</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditQuestions.map((q) => (
                      <tr key={q.number} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px", fontWeight: 600, color: C.navy }}>Q{q.number}</td>
                        <td style={{ padding: "8px 12px", color: "#1e293b" }}>{q.text}</td>
                        <td style={{ padding: "8px 12px", color: "#9ca3af", fontSize: 12 }}>{q.language}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Context Blob Dialog */}
      {showContextDialog && (
        <div onClick={() => setShowContextDialog(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", zIndex: 1000, display: "grid", placeItems: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, maxWidth: 900, width: "100%", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: C.navy }}>Context Blob</h2>
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {contextLayers ? `${(contextLayers.total_chars / 1000).toFixed(0)}K characters across ${contextLayers.layers?.length || 0} layers` : "Loading..."}
                </p>
              </div>
              <button onClick={() => setShowContextDialog(false)} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#9ca3af", padding: "4px 8px" }}>&times;</button>
            </div>
            <div style={{ overflow: "auto", padding: "16px 24px" }}>
              {contextLoading ? (
                <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>Loading context blob...</div>
              ) : contextLayers?.layers?.length > 0 ? (
                contextLayers.layers.map((layer, i) => (
                  <details key={i} style={{ marginBottom: 12, border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}
                    open={i === 0}>
                    <summary style={{
                      padding: "12px 16px", background: "#f8fafc", cursor: "pointer", fontWeight: 600, fontSize: 14, color: C.navy,
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                      <span>{layer.title}</span>
                      <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>{(layer.content.length / 1000).toFixed(1)}K chars</span>
                    </summary>
                    <pre style={{
                      padding: 16, margin: 0, fontSize: 11, lineHeight: 1.5, color: "#374151",
                      background: "#fafbfc", overflow: "auto", maxHeight: 400, whiteSpace: "pre-wrap", wordBreak: "break-word",
                      fontFamily: "'SF Mono', Monaco, Consolas, monospace",
                    }}>
                      {layer.content}
                    </pre>
                  </details>
                ))
              ) : (
                <p style={{ color: "#9ca3af", fontSize: 13 }}>No context blob available for this session.</p>
              )}
            </div>
          </div>
        </div>
      )}
      <AnswerDrawer
        open={!!activeAnswer && !!activeEntry}
        qNum={activeAnswer?.qNum}
        llm={activeAnswer?.llm}
        entry={activeEntry}
        onClose={() => setActiveAnswer(null)}
      />
      <AdminMetaDrawer
        open={showAdminDrawer}
        onClose={() => setShowAdminDrawer(false)}
        session={session}
        brandName={brandName}
        modelName={modelName}
        sessionDate={sessionDate}
        adminCards={adminCards}
        auditQuestionsCount={auditQuestions.length}
        onShowQuestions={() => {
          setShowAdminDrawer(false);
          setShowQuestionsDialog(true);
        }}
        onShowContext={async () => {
          setShowAdminDrawer(false);
          setShowContextDialog(true);
          if (!contextLayers) {
            setContextLoading(true);
            try {
              const { copilotGetContextBlob } = await import("../services/api");
              const data = await copilotGetContextBlob(sessionId);
              setContextLayers(data);
            } catch {
              setContextLayers({ layers: [], total_chars: 0 });
            } finally {
              setContextLoading(false);
            }
          }
        }}
      />
    </div>
    </QuoteRefContext.Provider>
  );
}
